import React, { useState, useEffect } from 'react';
import './transactionHistory.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSearch } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios'
import UserResponsePopup from '../shared/Popup/UserPopup';
import Select from 'react-select';
import { useAuth } from '../../context/AuthContext';

interface Purchase {
    id: string;
    datetime: string;
    description: string;
    amount: number;
    paidBy: string;
    participants: string[];
}

interface UserOption {
    value: string;
    label: string;
}

const PurchaseHistory: React.FC = () => {
    const { isLoggedIn } = useAuth();
    const [purchases, setPurchases] = useState<Purchase[]>([]);
    const [filteredPurchases, setFilteredPurchases] = useState<Purchase[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isResponsePopupOpen, setIsResponsePopupOpen] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [filterName, setFilterName] = useState('');
    const [userNames, setUserNames] = useState<string[]>([]);
    const [selectedOption, setSelectedOption] = useState<UserOption | null>(null);
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);

    useEffect(() => {
        getPurchases();
    }, []);

    useEffect(() => {
        if (filterName) {
            const normalizedFilter = filterName.toLowerCase();
            const filtered = purchases.filter(purchase =>
                purchase.paidBy.toLowerCase().includes(normalizedFilter) ||
                purchase.participants.some(participant => participant.toLowerCase().includes(normalizedFilter))
            );
            setFilteredPurchases(filtered);
        } else {
            setFilteredPurchases(purchases);
        }
    }, [filterName, purchases]);

    useEffect(() => {
        const names = new Set<string>();
        purchases.forEach(purchase => {
            if (purchase.paidBy) {
                names.add(purchase.paidBy);
            }
            purchase.participants.forEach(participant => {
                if (participant) {
                    names.add(participant);
                }
            });
        });

        const options = Array.from(names).sort().map(name => ({
            value: name,
            label: name
        }));

        setUserOptions(options);
    }, [purchases]);

    const getPurchases = async () => {
        try {
            setIsLoading(true);
            const result = await axios.get('https://kaskas.pro/split-bill-server/get-purchases');
            console.log(result);
            const transformedPurchases = result.data.map((purchase: any) => ({
                id: purchase.id,
                datetime: purchase.timestamp,
                amount: purchase.amount,
                paidBy: purchase.paid_user_name ?? '',
                participants: Array.isArray(purchase.participant_names)
                    ? purchase.participant_names.filter((name: unknown): name is string => typeof name === 'string')
                    : [],
                description: purchase.description
            }));
            setPurchases(transformedPurchases);
        } catch (error) {
            console.log(error);
            setResponseMessage("אירעה שגיאה בשליפת הנתונים");
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
        } finally {
            setIsLoading(false);
        }

    }

    const onDelete = async (id: string) => {
        console.log(`Delete: ${id}`);

        try {
            setIsLoading(true);
            const result = await axios.post('https://kaskas.pro/split-bill-server/delete-purchase', { id })
            setResponseMessage("קנייה נמחקה בהצלחה!");
            setIsSuccess(result.data.success);
            setIsResponsePopupOpen(true);
            await getPurchases();
        } catch (error) {
            setResponseMessage("אירעה שגיאה במחיקת הקנייה");
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
        } finally {
            setIsLoading(false);
        }
    }

    const formatDatetime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('he-IL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleCloseResponsePopup = () => {
        setIsResponsePopupOpen(false);
    };

    const handleSelectChange = (option: UserOption | null) => {
        setSelectedOption(option);
        setFilterName(option ? option.value : '');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterName(e.target.value);
        setSelectedOption(null);
    };

    return (
        <div className="transaction-history">
            {isLoading && (
                <div className='loading-overlay'>
                    <div className='loading-spinner'></div>
                </div>
            )}
            <h2 className="transaction-history__title">היסטוריית קניות משותפות</h2>

            <div className="filter-container">
                <div className="filter-input-group">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        type="text"
                        placeholder="סנן לפי שם משתמש..."
                        value={filterName}
                        onChange={handleInputChange}
                        className="filter-input"
                    />
                </div>

                <div className="filter-select-container">
                    <Select
                        options={userOptions}
                        value={selectedOption}
                        onChange={handleSelectChange}
                        placeholder="בחר משתמש"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
            </div>

            <div className="transaction-list purchase-list">
                {filteredPurchases.length > 0 ? (
                    filteredPurchases.map((purchase) => (
                        <div key={purchase.id} className="transaction-card">
                            <div className="transaction-card__header">
                                <span className="transaction-card__date">
                                    {formatDatetime(purchase.datetime)}
                                </span>
                                {isLoggedIn && (
                                    <button
                                        className="transaction-card__delete"
                                        onClick={() => onDelete(purchase.id)}
                                        aria-label="מחק"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                            <div className="transaction-card__body">
                                <div className="transaction-card__description">
                                    {purchase.description || "ללא תיאור קנייה"}
                                </div>
                                <div className="transaction-card__details">
                                    <div className="transaction-card__paid-by">
                                        שולם ע"י: {purchase.paidBy}
                                    </div>
                                    <div className="transaction-card__amount">
                                        ₪{purchase.amount}
                                    </div>
                                </div>
                                <div className="transaction-card__participants">
                                    <span>משתתפים:</span>
                                    <div className="participants-list">
                                        {purchase.participants.map((participant, index) => (
                                            <span key={index} className="participant-tag">
                                                {participant}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-filter-results">
                        <span className="empty-filter-icon">🔍</span>
                        <p>לא נמצאו קניות התואמות את החיפוש</p>
                    </div>
                )}
            </div>
            <UserResponsePopup
                isOpen={isResponsePopupOpen}
                isSuccess={isSuccess}
                message={responseMessage}
                onClose={handleCloseResponsePopup}
            />
        </div>
    );
};

export default PurchaseHistory; 